import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { cn } from '@/shared/lib/cn';
import { passbookCurrency } from '@/shared/lib/passbook-utils';
import type { Passbook } from '@/shared/types';
import {
  parseAddFieldOptions,
  visibleAddFields,
} from '../model/operator-add-fields';
import { OperatorMoneyField } from './operator-money-field';

interface OperatorAddFieldsProps {
  passbook: Passbook;
  values: Record<string, string>;
  onChange: (slug: string, value: string) => void;
  errors?: Record<string, string>;
}

const fieldControlClass = 'h-11 rounded-2xl';

export function OperatorAddFields({
  passbook,
  values,
  onChange,
  errors,
}: OperatorAddFieldsProps) {
  const fields = visibleAddFields(passbook);
  if (fields.length === 0) return null;

  const currencyCode = passbookCurrency(passbook);

  return (
    <div className="space-y-4">
      {fields.map((field) => {
        const slug = field.slug;
        const type = (field.type ?? 'text').toLowerCase();
        const value = values[slug] ?? '';
        const error = errors?.[slug];
        const label = (
          <Label htmlFor={`add-field-${slug}`} className="text-sm">
            {field.label || slug}
            {field.required ? <span className="text-destructive"> *</span> : null}
          </Label>
        );

        if (type === 'money') {
          return (
            <div key={slug} className="space-y-1">
              <OperatorMoneyField
                id={`add-field-${slug}`}
                label={field.label || slug}
                value={value}
                onChange={(v) => onChange(slug, v)}
                currencyCode={currencyCode}
                size="md"
                invalid={Boolean(error)}
              />
              {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
            </div>
          );
        }

        if (type === 'textarea') {
          return (
            <div key={slug} className="space-y-1.5">
              {label}
              <Textarea
                id={`add-field-${slug}`}
                value={value}
                onChange={(e) => onChange(slug, e.target.value)}
                className={cn(
                  'min-h-[88px] resize-y rounded-2xl',
                  error && 'border-destructive focus-visible:ring-destructive/40',
                )}
                aria-invalid={Boolean(error)}
              />
              {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
            </div>
          );
        }

        if (type === 'select') {
          const options = parseAddFieldOptions(field.options);
          return (
            <div key={slug} className="space-y-1.5">
              {label}
              <Select
                value={value || undefined}
                onValueChange={(next) => onChange(slug, next)}
              >
                <SelectTrigger
                  id={`add-field-${slug}`}
                  className={cn(
                    fieldControlClass,
                    error && 'border-destructive focus:ring-destructive/40',
                  )}
                  aria-invalid={Boolean(error)}
                >
                  <SelectValue placeholder="Selecione…" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
            </div>
          );
        }

        if (type === 'radio') {
          const options = parseAddFieldOptions(field.options);
          return (
            <div key={slug} className="space-y-2">
              {label}
              <RadioGroup
                value={value || undefined}
                onValueChange={(next) => onChange(slug, next)}
                className="flex flex-wrap gap-2"
                aria-invalid={Boolean(error)}
              >
                {options.map((opt) => {
                  const optId = `add-field-${slug}-${opt}`;
                  return (
                    <Label
                      key={opt}
                      htmlFor={optId}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm font-normal',
                        value === opt
                          ? 'border-primary bg-primary/10'
                          : 'border-[hsl(var(--glass-border)/0.45)] hover:bg-accent/40',
                      )}
                    >
                      <RadioGroupItem value={opt} id={optId} />
                      {opt}
                    </Label>
                  );
                })}
              </RadioGroup>
              {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
            </div>
          );
        }

        return (
          <div key={slug} className="space-y-1.5">
            {label}
            <Input
              id={`add-field-${slug}`}
              type={type === 'number' ? 'number' : 'text'}
              value={value}
              onChange={(e) => onChange(slug, e.target.value)}
              className={cn(
                fieldControlClass,
                error && 'border-destructive focus-visible:ring-destructive/40',
              )}
              inputMode={type === 'number' ? 'numeric' : undefined}
              aria-invalid={Boolean(error)}
            />
            {error ? <p className="text-sm font-medium text-destructive">{error}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
